import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  AudioControlIcon,
  AudioMuteIcon,
  AudioPauseIcon,
  AudioPlayIcon,
} from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import type { RootState } from '../../../../app';
import { selectTranscriptionForCall } from '../tabs/TranscriptTab/transcriptionSlice';
import { AudioInProgress, AudioError } from './AudioMessages';
import {
  AudioPlayerContainer,
  HiddenAudio,
  ProgressBarContainer,
  ProgressBar,
  PlayerControls,
  PlayerContainer,
} from './AudioPlayer.styles';

const COMPLETE = 'complete';
const PENDING = 'pending';
const IN_PROGRESS = 'in_progress';
const UNAVAILABLE = 'unavailable';

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 1000;

interface AudioPlayerProps {
  callerId: string;
}

/**
 * AudioPlayer component to play audio associated with a call.
 */
const AudioPlayer = ({ callerId }: AudioPlayerProps) => {
  /* https://developer.mozilla.org/en-US/docs/Web/HTML/Element/audio */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /* keep these as local state for now, will need to check how we are getting the source  */
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioInProgress, setAudioInProgress] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  const audioData = useSelector(
    (state: RootState) =>
      selectTranscriptionForCall(callerId)(state)?.payload?.audio_data,
  );

  const { status = '', audio_url = '' } = audioData || {};

  const resetPlayerState = useCallback(() => {
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioInProgress(0);
    setIsPlaying(false);
    setRetryCount(0);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && !Number.isFinite(audio.duration)) {
        // Sometimes the audio element decides the audio is "streaming" (i.e.
        // the browser-recorded audio from the debug panel). Forcing the element
        // to scan ahead will cause the duration to get updated.
        audio.currentTime = Number.MAX_SAFE_INTEGER;
        return;
      }
      setAudioDuration(audio.duration);
    };
    const handleTimeUpdate = () => {
      let progress = 0;
      if (audio.duration && !isNaN(audio.duration)) {
        progress = (audio.currentTime / audio.duration) * 100;
        progress = Math.max(0, Math.min(100, progress));
      }

      // in handleLoadedMetadata we set currentTime to a large number to force duration calculation
      // reset it back to 0 once we have the duration
      if (progress === 100) {
        audio.currentTime = 0;
      }

      setAudioDuration(audio.duration);
      setAudioCurrentTime(audio.currentTime);
      setAudioInProgress(progress);
    };
    const handleEnded = () => {
      resetPlayerState();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audio_url, audioRef, resetPlayerState]);

  useEffect(() => {
    if (audioRef.current && audio_url) {
      audioRef.current.load();
      resetPlayerState();
    }
  }, [audio_url, resetPlayerState]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
      if (audio.duration === 0 && retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          audio.load();
        }, RETRY_DELAY_MS);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audio_url, retryCount]);

  const handlePlayPause = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(prev => !prev);
    }
  }, [isPlaying]);

  const handleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(prev => !prev);
    }
  }, [isMuted]);

  interface ProgressClickEvent {
    currentTarget: HTMLElement;
    clientX: number;
  }

  const handleProgressClick = useCallback(
    (e: ProgressClickEvent) => {
      if (!audioRef.current) return;
      const progressBar = e.currentTarget;
      const clickPosition =
        e.clientX - progressBar.getBoundingClientRect().left;
      const clickPercentage = (clickPosition / progressBar.offsetWidth) * 100;
      const clampedPercentage = Math.max(0, Math.min(100, clickPercentage));
      const newTime = (clampedPercentage / 100) * audioDuration;
      audioRef.current.currentTime = Math.max(
        0,
        Math.min(audioDuration, newTime),
      );
      setAudioInProgress(clampedPercentage);
    },
    [audioDuration],
  );

  if (status === IN_PROGRESS) {
    return <AudioInProgress />;
  }

  if (!audio_url || !status || status === PENDING || status === UNAVAILABLE) {
    // NOTE: status === PENDING sounds like it isn't an error, but it can happen
    //   when rtt_egress is unable to get a signed URL from finch.
    // eslint-disable-next-line no-console
    console.warn('AudioPlayer: Audio unavailable:', {
      status,
      audio_url,
    });
    return <AudioError />;
  }

  return (
    <AudioPlayerContainer>
      <HiddenAudio ref={audioRef} src={audio_url} />
      {status === COMPLETE && (
        <PlayerContainer data-name="audio-player-container">
          <PlayerControls horizontal>
            <Box onClick={handlePlayPause} data-name="audio-play-pause-button">
              {isPlaying ? <AudioPauseIcon /> : <AudioPlayIcon />}
            </Box>
            <Text paddingLeft="8px" color="secondaryText">
              {formatDuration(audioCurrentTime)} /{' '}
              {formatDuration(audioDuration)}
            </Text>
            <ProgressBarContainer onClick={handleProgressClick}>
              <ProgressBar progress={audioInProgress} />
            </ProgressBarContainer>
            <Box onClick={handleMute}>
              {isMuted ? <AudioMuteIcon /> : <AudioControlIcon />}
            </Box>
          </PlayerControls>
        </PlayerContainer>
      )}
    </AudioPlayerContainer>
  );
};

/**
 * Formats time duration in seconds into MM:SS string format.
 * @param {number} seconds: The time duration in seconds
 * @returns {string} The formatted (MM:SS) time string
 * @note Handles NaN and Infinity since HTML's audio element's duration can
 *   return them per
 *   https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/duration
 */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${numberString(minutes)}:${numberPadded(remainingSeconds)}`;
}

function numberString(num: number): string {
  return Number.isFinite(num) ? num.toString() : '--';
}

function numberPadded(num: number, maxLength = 2): string {
  return Number.isFinite(num) ? num.toString().padStart(maxLength, '0') : '--';
}

export default AudioPlayer;
